import React from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Grid, TextField, FormControl, InputLabel, 
    Select, MenuItem, Box, Typography 
} from '@mui/material';
import FinancialBreakdown from './FinancialBreakdown';
import FormulaVisualization from './FormulaVisualization';

const ActivityDialog = ({ 
    open, 
    onClose, 
    onSave, 
    editingActivity, 
    activityForm, 
    setActivityForm, 
    forestStands, 
    financialItems, 
    handleFinancialItemChange, 
    handleRemoveFinancialItem, 
    handleAddFinancialItem,
    calculationParams,
    handleParamChange,
    updateSuggestedFinancials,
    userSettings
}) => {
    // Refresh automated financials when stand or type changes
    React.useEffect(() => {
        if (!editingActivity && open) {
            updateSuggestedFinancials(forestStands, userSettings);
        }
    }, [activityForm.stand_id, activityForm.activity_type, open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {editingActivity ? `Edit Activity: ${editingActivity.activity_type}` : 'Plan New Activity'}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Forest Stand</InputLabel>
                            <Select
                                value={forestStands?.features?.some(f => f.id === activityForm.stand_id) ? activityForm.stand_id : ''}
                                label="Forest Stand"
                                onChange={(e) => setActivityForm({ ...activityForm, stand_id: e.target.value })}
                            >
                                {forestStands?.features?.map(f => {
                                    const area = f.properties?.area_hectares;
                                    const formattedArea = (area !== undefined && area !== null) ? Number(area).toFixed(2) : '0.00';
                                    return (
                                        <MenuItem key={f.id} value={f.id}>
                                            Stand {f.properties?.stand_number} ({formattedArea} ha)
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Activity Type</InputLabel>
                            <Select
                                value={activityForm.activity_type}
                                label="Activity Type"
                                onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                            >
                                <MenuItem value="Planting">Planting</MenuItem>
                                <MenuItem value="Clearing">Clearing</MenuItem>
                                <MenuItem value="Thinning">Thinning</MenuItem>
                                <MenuItem value="Final Felling">Final Felling</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Sub-type (optional)"
                            value={activityForm.sub_type}
                            onChange={(e) => setActivityForm({ ...activityForm, sub_type: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Planned Year"
                            type="number"
                            value={activityForm.planned_year}
                            onChange={(e) => setActivityForm({ ...activityForm, planned_year: parseInt(e.target.value) })}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel>Planned Quarter</InputLabel>
                            <Select
                                value={activityForm.planned_quarter}
                                label="Planned Quarter"
                                onChange={(e) => setActivityForm({ ...activityForm, planned_quarter: parseInt(e.target.value) })}
                            >
                                <MenuItem value={1}>Q1 (Jan-Mar)</MenuItem>
                                <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                                <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                                <MenuItem value={4}>Q4 (Oct-Dec)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={activityForm.priority}
                                label="Priority"
                                onChange={(e) => setActivityForm({ ...activityForm, priority: e.target.value })}
                            >
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Formula Visualization Block */}
                    {activityForm.stand_id && userSettings && !editingActivity && (
                        <Grid item xs={12}>
                            <FormulaVisualization 
                                activityType={activityForm.activity_type}
                                calculationParams={calculationParams}
                                handleParamChange={handleParamChange}
                                forestStands={forestStands}
                                standId={activityForm.stand_id}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FinancialBreakdown 
                            financialItems={financialItems}
                            handleFinancialItemChange={handleFinancialItemChange}
                            handleRemoveFinancialItem={handleRemoveFinancialItem}
                            handleAddFinancialItem={handleAddFinancialItem}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={activityForm.description}
                            onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={onSave} color="primary">
                    {editingActivity ? 'Update Activity' : 'Create Activity'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ActivityDialog;
